/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable no-unused-expressions */
import React, { useState, useEffect, useCallback } from 'react';
import { useDropzone, FileRejection } from 'react-dropzone';
import InsertDriveFileIcon from '@material-ui/icons/InsertDriveFile';
// import LinkIcon from '@material-ui/icons/Link';
import CloseIcon from '@material-ui/icons/Close';
import PublishIcon from '@material-ui/icons/Publish';
import LockIcon from '@material-ui/icons/Lock';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import { Grid, InputLabel, IconButton } from '@material-ui/core';
import Swal from 'sweetalert2';
// import compressImage from 'utils/imageCompression';
import clsx from 'clsx';
import { Skeleton } from '@material-ui/lab';
import { UploadAdapter } from 'components/TextEditor/UploadAdapter';
import { validateUrl, checkUrl } from '../../utils/validateUrl';
import { checkImageWidthAndHeight } from '../../utils/checkImageWidthAndHeight';
// import { toBase64 } from '../../utils/base64';
import { Toast } from '../Toast';
import useStyles from './styles';
import { IImageUploadPreviewerProps } from './interfaces';

const ONE_MB_IN_BYTES = 1 * 1024 * 1024;

const ImageUploadPreviewer: React.FC<IImageUploadPreviewerProps> = ({
  permissions = {
    canEdit: true,
    canViewImageLink: true,
  },
  externalErrorControl,
  onError,
  onSelect,
  name,
  initialValue,
  isToShowBase64Error,
  siteDomain,
}) => {
  const classes = useStyles();

  const [imageLoaded, setImageLoaded] = useState(false);
  const [image, setImage] = useState('');
  const [imageName, setImageName] = useState('');
  const [_hasError, _setHasError] = useState(false);

  const onDrop = useCallback((acceptedFiles) => {
    handleFileChange(acceptedFiles[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onDropRejected = (fileRejections: FileRejection[]) => {
    console.log('rejected', fileRejections);
    const { errors } = fileRejections[0];
    const errorMessage = getFriendlyErrorMessage(errors[0].code);
    setHasError(true);
    Toast(errorMessage, 'error');
  };

  const { getRootProps, isDragActive } = useDropzone({
    accept: ['image/png', 'image/jpg', 'image/jpeg', 'image/webp'],
    onDrop,
    onDropRejected,
    noClick: true,
    maxSize: ONE_MB_IN_BYTES,
    maxFiles: 1,
    disabled: !permissions.canEdit,
  });

  useEffect(() => {
    if (initialValue) {
      setImage(initialValue);
    }
  }, []);

  const hasError = () => {
    return externalErrorControl?.error || _hasError;
  };

  const setHasError = (newValue: boolean) => {
    externalErrorControl?.setError(newValue) || _setHasError(newValue);
  };

  const handleFileChange = async (file?: File) => {
    try {
      if (file) {
        await checkImageWidthAndHeight(file, null);

        // const fileSizeInMb = file.size / 1024 / 1024;
        // if (fileSizeInMb > 1) {
        //   throw new Error('File must be smaller than 1MB');
        // }
        setHasError(false);

        // const compressedImage = await compressImage(file);
        // const base64Image = await toBase64(compressedImage);

        const uploadAdapter = new UploadAdapter({ file }, siteDomain);
        const image = await uploadAdapter.upload();

        setImage(image.default);
        setImageName(file.name);
        onSelect?.(image.default, file.name);
      }
    } catch (error) {
      onError?.(error);
      setHasError(true);
      Toast(error, 'error');
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const selectByUrl = async () => {
    try {
      const { value: url } = await Swal.fire({
        title: 'Please, insert the image URL',
        input: 'url',
        inputPlaceholder: 'Insert URL',
        showCancelButton: true,
        confirmButtonColor: '#B63A59',
        reverseButtons: true,
        confirmButtonText: 'Confirm',
        customClass: {
          container: classes.swalDialog,
        },
        // eslint-disable-next-line no-confusing-arrow
        inputValidator: (result) =>
          !validateUrl(result) || !checkUrl(result)
            ? 'Please, insert a valid image URL'
            : '',
      });

      if (url) {
        await checkImageWidthAndHeight(null, url as string);

        Toast('Image url inserted with success!');
        setImage(url as string);
        setImageName(url as string);
        onSelect?.(url as string, url as string);
      }
    } catch (error) {
      onError?.(error);
      setHasError(true);
      Toast(error, 'error');
    }
  };

  const openFileModal = () => {
    document.getElementById('upload-image')?.click();
  };

  const removeImage = () => {
    setImage('');
    setImageName('');
    onSelect?.('', '');
  };

  const getResumedImageName = () => {
    if (imageName.length > 50) {
      const extensionPointIndex = imageName.lastIndexOf('.');
      const name = imageName.slice(0, extensionPointIndex);
      const extension = imageName.slice(extensionPointIndex + 1);

      return `${name.slice(0, 50)}...${extension}`;
    }

    return imageName;
  };

  const renderDropzone = () => {
    return (
      <div className={classes.dropzone} style={{ height: image ? 200 : 91 }}>
        <InsertDriveFileIcon className={classes.icon} />
        <span>Drop the file here</span>
      </div>
    );
  };

  const getFriendlyErrorMessage = (errorCode: string) => {
    switch (errorCode) {
      case 'file-invalid-type':
        return 'This file type is invalid, use only images';
      case 'file-too-large':
        return 'File must be smaller than 1MB';
      default:
        return 'An error occurred while selecting this file';
    }
  };

  return (
    <div className={classes.topLevelContainer}>
      <Grid item xs={12}>
        <InputLabel error={hasError()} className={classes.inputLabel}>
          {name}
        </InputLabel>
      </Grid>
      <input
        style={{ display: 'none' }}
        id="upload-image"
        name="upload-image"
        type="file"
        onChange={(ev) => handleFileChange(ev.target.files?.[0])}
        accept=".png, .jpg, .jpeg"
      />
      {image ? (
        <div
          {...getRootProps({
            className: classes.dropzoneContainer,
            style: {
              height:
                200 +
                (isToShowBase64Error ? 26 : 0) +
                (imageName + permissions.canViewImageLink ? 17 : 0),
            },
          })}
        >
          {isDragActive ? (
            renderDropzone()
          ) : (
            <>
              {isToShowBase64Error && (
                <div className={classes.base64Alert}>
                  <ErrorOutlineIcon style={{ fontSize: 14, marginRight: 4 }} />
                  <span>Image is currently base64</span>
                </div>
              )}
              <div
                className={clsx(
                  classes.imageContainer,
                  isToShowBase64Error && classes.imageContainerWithBase64,
                  hasError() && classes.containerError,
                )}
              >
                <img
                  className={classes.image}
                  src={image}
                  alt={name}
                  onLoad={() => setImageLoaded(true)}
                  style={{ display: imageLoaded ? 'flex' : 'none' }}
                />
                {!imageLoaded && (
                  <Skeleton
                    variant="rect"
                    animation="wave"
                    width={300}
                    height={200}
                  />
                )}
                <IconButton className={classes.closeIcon} onClick={removeImage}>
                  <CloseIcon />
                </IconButton>
                <button
                  className={classes.uploadButton}
                  type="button"
                  onClick={openFileModal}
                >
                  <PublishIcon />
                  <span>Select Other</span>
                </button>
              </div>
              {permissions.canViewImageLink && (
                <span className={classes.imageName}>
                  {getResumedImageName()}
                </span>
              )}
            </>
          )}
        </div>
      ) : permissions.canEdit ? (
        <div
          {...getRootProps({
            className: clsx(
              classes.dropzoneContainer,
              classes.dropzoneSelectContainer,
              hasError() && classes.containerError,
            ),
          })}
        >
          {isDragActive ? (
            renderDropzone()
          ) : (
            <>
              <div className={classes.selectContainer}>
                <button
                  type="button"
                  onClick={openFileModal}
                  className={classes.button}
                >
                  <InsertDriveFileIcon className={classes.icon} />
                  <span>Any image uploaded</span>
                  <span className={classes.details}>Click to upload</span>
                </button>
                {/* <button
                  type="button"
                  onClick={selectByUrl}
                  className={clsx(classes.button, classes.buttonRight)}
                >
                  <LinkIcon className={clsx(classes.icon, classes.iconRight)} />
                  <span>Or Link a URL</span>
                </button> */}
              </div>
            </>
          )}
        </div>
      ) : (
        <div className={classes.blockedContainer}>
          <LockIcon style={{ fontSize: 14, marginRight: 5 }} />
          <span>You don&apos;t have permission to do it</span>
        </div>
      )}
    </div>
  );
};

export default React.memo(ImageUploadPreviewer);
